/**
 * Color Picker Core Logic
 * Handles the main business logic for color selection and management
 */

import {
  rgbToHex,
  hsvToRgb,
  rgbToHsv,
  parseColor,
  formatColor,
  isValidColor,
} from './colorUtils.js';
import { favoritesManager } from './favoritesManager.js';

/**
 * Color Picker Core class
 */
export class ColorPickerCore {
  constructor(options = {}) {
    this.currentColor = { r: 255, g: 255, b: 255, a: 1 };
    this.currentHsv = { h: 0, s: 0, v: 1 };
    this.defaultColors = options.defaultColors || [];
    this.classicColors = options.classicColors || [];
    this.listeners = new Map();
    this.format = options.format || 'hex';

    this.events = {
      COLOR_CHANGED: 'colorChanged',
      FORMAT_CHANGED: 'formatChanged',
      FAVORITES_UPDATED: 'favoritesUpdated',
    };

    // Listen to favorites changes
    favoritesManager.addListener(() => {
      this.emit(this.events.FAVORITES_UPDATED, favoritesManager.getFavorites());
    });
  }

  /**
   * Set the current color
   * @param {string|object} color - Color as string or RGB object
   * @param {boolean} silent - Don't emit change event if true
   */
  setColor(color, silent = false) {
    let rgba;

    if (typeof color === 'string') {
      rgba = parseColor(color);
    } else if (color && typeof color === 'object') {
      rgba = {
        r: color.r || 0,
        g: color.g || 0,
        b: color.b || 0,
        a: color.a !== undefined ? color.a : 1,
      };
    } else {
      return false;
    }

    this.currentColor = rgba;
    this.currentHsv = rgbToHsv(rgba.r, rgba.g, rgba.b);

    if (!silent) {
      this.emit(this.events.COLOR_CHANGED, this.getCurrentColor());
    }

    return true;
  }

  /**
   * Get the current color in the specified format
   * @param {string} format - Output format ('hex', 'rgb', 'rgba', 'object')
   * @returns {string|object} Current color
   */
  getCurrentColor(format = this.format) {
    const { r, g, b, a } = this.currentColor;

    switch (format) {
      case 'object':
        return { r, g, b, a };
      case 'rgb':
        return formatColor(r, g, b, a, 'rgb');
      case 'rgba':
        return formatColor(r, g, b, a, 'rgba');
      case 'hex':
      default:
        return formatColor(r, g, b, a, 'hex');
    }
  }

  /**
   * Set color from HSV values
   * @param {number} h - Hue (0-360)
   * @param {number} s - Saturation (0-1)
   * @param {number} v - Value (0-1)
   * @param {boolean} silent - Don't emit change event if true
   */
  setColorFromHsv(h, s, v, silent = false) {
    this.currentHsv = { h, s, v };
    const rgb = hsvToRgb(h, s, v);
    this.currentColor = { ...rgb, a: this.currentColor.a };

    if (!silent) {
      this.emit(this.events.COLOR_CHANGED, this.getCurrentColor());
    }
  }

  /**
   * Get current HSV values
   * @returns {{h: number, s: number, v: number}} HSV object
   */
  getCurrentHsv() {
    return { ...this.currentHsv };
  }

  /**
   * Set the alpha (opacity) value
   * @param {number} alpha - Alpha value (0-1)
   * @param {boolean} silent - Don't emit change event if true
   */
  setAlpha(alpha, silent = false) {
    this.currentColor.a = Math.max(0, Math.min(1, alpha));

    if (!silent) {
      this.emit(this.events.COLOR_CHANGED, this.getCurrentColor());
    }
  }

  /**
   * Get the current alpha value
   * @returns {number} Alpha value (0-1)
   */
  getAlpha() {
    return this.currentColor.a;
  }

  /**
   * Set the output format
   * @param {string} format - Format ('hex', 'rgb', 'rgba')
   */
  setFormat(format) {
    if (['hex', 'rgb', 'rgba'].includes(format)) {
      this.format = format;
      this.emit(this.events.FORMAT_CHANGED, format);
    }
  }

  /**
   * Get the current format
   * @returns {string} Current format
   */
  getFormat() {
    return this.format;
  }

  /**
   * Set default colors
   * @param {string[]} colors - Array of color strings
   */
  setDefaultColors(colors) {
    if (Array.isArray(colors)) {
      this.defaultColors = colors.filter((color) => isValidColor(color));
    }
  }

  /**
   * Get default colors
   * @returns {string[]} Array of default colors
   */
  getDefaultColors() {
    return [...this.defaultColors];
  }

  /**
   * Set classic colors
   * @param {string[]} colors - Array of color strings
   */
  setClassicColors(colors) {
    if (Array.isArray(colors)) {
      this.classicColors = colors.filter((color) => isValidColor(color));
    }
  }

  /**
   * Get classic colors
   * @returns {string[]} Array of classic colors
   */
  getClassicColors() {
    return [...this.classicColors];
  }

  /**
   * Add current color to favorites
   * @returns {boolean} True if added successfully
   */
  addToFavorites() {
    return favoritesManager.addFavorite(this.getCurrentColor('hex'));
  }

  /**
   * Remove current color from favorites
   * @returns {boolean} True if removed successfully
   */
  removeFromFavorites() {
    return favoritesManager.removeFavorite(this.getCurrentColor('hex'));
  }

  /**
   * Toggle current color in favorites
   * @returns {boolean} New favorite status
   */
  toggleFavorite() {
    return favoritesManager.toggleFavorite(this.getCurrentColor('hex'));
  }

  /**
   * Check if current color is favorited
   * @returns {boolean} True if favorited
   */
  isCurrentColorFavorited() {
    return favoritesManager.isFavorite(this.getCurrentColor('hex'));
  }

  /**
   * Get all favorite colors
   * @returns {string[]} Array of favorite colors
   */
  getFavorites() {
    return favoritesManager.getFavorites();
  }

  /**
   * Generate a palette of colors
   * @param {string} baseColor - Base color for palette generation
   * @param {string} type - Palette type ('analogous', 'complementary', 'triadic', 'monochromatic')
   * @returns {string[]} Array of palette colors
   */
  generatePalette(baseColor, type = 'analogous') {
    const rgb = parseColor(baseColor);
    const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
    const palette = [];

    switch (type) {
      case 'analogous':
        for (let i = -2; i <= 2; i++) {
          const h = (hsv.h + i * 30 + 360) % 360;
          const color = hsvToRgb(h, hsv.s, hsv.v);
          palette.push(rgbToHex(color.r, color.g, color.b));
        }
        break;

      case 'complementary': {
        palette.push(baseColor);
        const compH = (hsv.h + 180) % 360;
        const compColor = hsvToRgb(compH, hsv.s, hsv.v);
        palette.push(rgbToHex(compColor.r, compColor.g, compColor.b));
        break;
      }

      case 'triadic':
        for (let i = 0; i < 3; i++) {
          const h = (hsv.h + i * 120) % 360;
          const color = hsvToRgb(h, hsv.s, hsv.v);
          palette.push(rgbToHex(color.r, color.g, color.b));
        }
        break;

      case 'monochromatic':
        for (let i = 0; i < 5; i++) {
          const v = Math.max(0.2, Math.min(1, hsv.v - i * 0.2));
          const color = hsvToRgb(hsv.h, hsv.s, v);
          palette.push(rgbToHex(color.r, color.g, color.b));
        }
        break;
    }

    return palette;
  }

  /**
   * Add event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  /**
   * Emit event to listeners
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.warn(
            `Errore nel listener del color picker per ${event}:`,
            error
          );
        }
      });
    }
  }

  /**
   * Reset to default state
   */
  reset() {
    this.setColor('#ffffff', true);
    this.setFormat('hex');
  }

  /**
   * Validate a color string
   * @param {string} color - Color string to validate
   * @returns {boolean} True if valid
   */
  validateColor(color) {
    return isValidColor(color);
  }

  /**
   * Get color info for display
   * @returns {object} Color information object
   */
  getColorInfo() {
    const { r, g, b, a } = this.currentColor;
    const { h, s, v } = this.currentHsv;

    return {
      rgb: { r, g, b },
      rgba: { r, g, b, a },
      hsv: { h: Math.round(h), s: Math.round(s * 100), v: Math.round(v * 100) },
      hex: this.getCurrentColor('hex'),
      css: {
        rgb: formatColor(r, g, b, a, 'rgb'),
        rgba: formatColor(r, g, b, a, 'rgba'),
        hex: formatColor(r, g, b, a, 'hex'),
      },
      isFavorited: this.isCurrentColorFavorited(),
    };
  }
}
